import {
  Grid,
  Button,
  Typography,
  ThemeProvider,
  createTheme,
  Box,
} from "@mui/material";
import { useContext, useMemo, useState } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { useTranslation } from "react-i18next";
import { ValidatePDFContext } from "../../../contexts/ValidatePDFContext";
import { getExamAlgorithmTranslation } from "../../../utils/examReportsHelper";
import { ReportDataContext } from "../../../contexts/ReportDataContext";
import { ReportEachSectionContext } from "../../../contexts/CommentContext";
import useClinic from "../../../hooks/useClinic";
import AddCommentModal from "../AddCommentModal";
import { addCommentReport } from "../../../services/Exam";
import { getLocalDate } from "../../../utils/dateHelper";
import { SpeechBubbleIcon } from "../../../assets/icons";
import { ModalCustomDark } from "../../../styles/muiStylesHelper";
import { getDecimalNum } from "../../../utils/numberHelper";

const CustomTypography = ({ title, body, isBlack = false }) => {
  const textColor = useMemo(
    () => (isBlack ? "text-black" : "text-[#4471DB]"),
    [isBlack]
  );
  const memoized = useMemo(
    () => (
      <Typography variant="body1" mb={0.5} fontSize="14px">
        <b>{title}</b>: <span className={textColor}>{body}</span>
      </Typography>
    ),
    [title, textColor, body]
  );
  return memoized;
};

export const ReportInfoHeaderComponent = ({
  isWiderThanSmallScreen,
  patient,
  rightText,
  handleClickOpen,
}) => {
  const { t } = useTranslation();
  const { clinicName, clinicTimezone } = useClinic();
  const { forBackendPdf } = useContext(ValidatePDFContext);

  return (
    <Grid container item py={1} px={1} display="flex" flexDirection="column">
      <Box
        display="flex"
        justifyContent="space-between"
        width="100%"
        alignItems={{ xs: "flex-start", sm: "center" }}
        flexDirection={{ xs: "column", sm: "row" }}
      >
        <Box display="flex" flexDirection="column">
          <Typography
            variant={isWiderThanSmallScreen ? "h6" : "body1"}
            fontWeight="bold"
            fontSize={18}
            maxWidth="300px"
            overflow="hidden"
            whiteSpace="nowrap"
            textOverflow="ellipsis"
          >
            {`${t("devices_table_name")}: ${patient?.firstName} ${patient?.lastName}`}
          </Typography>
          <Box display="flex" flexDirection="row" width="100%" gap={1}>
            <Typography variant="body1" fontSize={14}>
              <span
                style={{ fontWeight: "bold" }}
              >{`${t("patients_modal_sex")}: `}</span>
              <span>{patient?.sex}</span>
            </Typography>
            <Typography variant="body1" fontSize={14}>
              <span
                style={{ fontWeight: "bold" }}
              >{`${t("report_age")}: `}</span>
              <span>{patient?.age}</span>
            </Typography>
            <Typography variant="body1" fontSize={14}>
              <span
                style={{ fontWeight: "bold" }}
              >{`${t("patients_modal_birth")}: `}</span>
              <span>{getLocalDate(patient?.dateOfBirth, clinicTimezone)}</span>
            </Typography>
          </Box>
          <Box display="flex" flexDirection="column" width="100%">
            {(patient?.rightSphere !== null ||
              patient?.rightCylinder !== null ||
              patient?.rightAxis !== null) && (
              <Typography variant="body1" fontSize={14}>
                <span style={{ fontWeight: "bold" }}>{`${t("OD Rx")}: `}</span>
                <span>
                  {patient?.rightSphere !== null
                    ? getDecimalNum(patient?.rightSphere, 2)
                    : "N/A"}
                  {" sph | "}
                  {patient?.rightCylinder !== null
                    ? getDecimalNum(patient?.rightCylinder, 2)
                    : "N/A"}
                  {" cyl | "}
                  {patient?.rightAxis !== null
                    ? `${getDecimalNum(patient?.rightAxis, 0)}°`
                    : "N/A"}
                  {` ${t("word_axis")}`}
                </span>
              </Typography>
            )}
            {(patient?.leftSphere !== null ||
              patient?.leftCylinder !== null ||
              patient?.leftAxis !== null) && (
              <Typography variant="body1" fontSize={14}>
                <span style={{ fontWeight: "bold" }}>{`${t("OS Rx")}: `}</span>
                <span>
                  {patient?.leftSphere !== null
                    ? getDecimalNum(patient?.leftSphere, 2)
                    : "N/A"}
                  {" sph | "}
                  {patient?.leftCylinder !== null
                    ? getDecimalNum(patient?.leftCylinder, 2)
                    : "N/A"}
                  {" cyl | "}
                  {patient?.leftAxis !== null
                    ? `${getDecimalNum(patient?.leftAxis, 0)}°`
                    : "N/A"}
                  {` ${t("word_axis")}`}
                </span>
              </Typography>
            )}
          </Box>
        </Box>
        <Box textAlign={{ xs: "left", sm: "right" }}>
          <Typography variant="body1" fontSize="14px">
            {clinicName}
          </Typography>
          <Typography variant="body1" fontSize="14px">
            {rightText}
          </Typography>
        </Box>
      </Box>
      {!forBackendPdf && (
        <Box display="flex" width="100%" mt={1} justifyContent="flex-end">
          <Button
            onClick={handleClickOpen}
            startIcon={<SpeechBubbleIcon />}
            sx={{
              ...ModalCustomDark(),
              width: { xs: "100%", sm: "fit-content" },
            }}
          >
            {t("word_add_comment")}
          </Button>
        </Box>
      )}
    </Grid>
  );
};

const ReportInfoHeaderContent = ({
  isWiderThanSmallScreen,
  examTypeTranslation,
}) => {
  const { getAccessTokenSilently } = useAuth0();
  const { t } = useTranslation();
  const { clinicId } = useClinic();
  const { exam, patient } = useContext(ReportDataContext);
  const { sectionId, examReport } = useContext(ReportEachSectionContext);

  const [open, setOpen] = useState(false);
  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  const submitComment = async (commentData) => {
    const token = await getAccessTokenSilently();
    return addCommentReport(
      token,
      clinicId,
      patient?.id,
      exam?.id,
      sectionId,
      commentData
    );
  };

  const examTranslation = useMemo(
    () => t(getExamAlgorithmTranslation(examReport?.exam?.algorithm)),
    [examReport, t]
  );

  return (
    <>
      <ReportInfoHeaderComponent
        isWiderThanSmallScreen={isWiderThanSmallScreen}
        patient={patient}
        rightText={`${examTypeTranslation ?? ""}${examTranslation}`}
        handleClickOpen={handleOpen}
      />
      <AddCommentModal
        isOpen={open}
        handleClose={handleClose}
        submitComment={submitComment}
      />
    </>
  );
};

const ReportInfoShared = ({ data = [], gridSizeData = [] }) => {
  const bpTheme = createTheme({
    breakpoints: {
      values: {
        xs: 0,
        sm: 600,
        md: 900,
        lg: 1300,
        xl: 1700,
      },
    },
  });
  const memoized = useMemo(() => {
    return (
      <ThemeProvider theme={bpTheme}>
        {data.map((group, index) => (
          <Grid
            item
            xs={gridSizeData?.[index]?.xs}
            sm={gridSizeData?.[index]?.sm}
            md={gridSizeData?.[index]?.md}
            lg={gridSizeData?.[index]?.lg}
            xl={gridSizeData?.[index]?.xl}
            key={index}
          >
            {group.map((item, itemIndex) => {
              if (!item) return <></>;

              return (
                <CustomTypography
                  key={itemIndex}
                  title={item?.title}
                  body={item?.body}
                  isBlack={item?.isBlack}
                />
              );
            })}
          </Grid>
        ))}
      </ThemeProvider>
    );
  }, [bpTheme, data, gridSizeData]);
  return memoized;
};

export { ReportInfoShared, ReportInfoHeaderContent };
